import React, { useState, useContext, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../../AppContext";
import * as actions from "../../actions/actions";
import Service from "../../services/service";
import LibraryTitle from '../../components/libraryTitleBar';
import LoadingScreen from '../../components/LoadingScreen.js';
import Pagination from '../../components/pagination';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Table from '../../components/table';
import Checkbox from '@material-ui/core/Checkbox';
import { getModuloColor, resolveNotificationMessage, timeConverter, clickDownloadLink, config } from '../../helpers/constants';
import { Paper, Link, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import LibraryDetails from '../../components/LibraryDetails';
import SearchIcon from '@material-ui/icons/Search';
import NewProjectForm from '../../components/NewProjectForm';
import IconLabel from '../../components/IconLabel';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import ShareIcon from "@material-ui/icons/Share";
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
    },
    title: {
        flex: "0 0 auto",
        height: 0,
    },
    body_container: {
        display: 'flex',
        flex: '1',
        flexDirection: 'row',
        position: "relative",
    },
    content: {
        position: "relative",
        flex: "1 1 auto",
        overflow: "hidden",
        overflowY: "auto",    // FIXME, showing temp scroll bar during loading in table vview
        padding: 0,
        height: 0,  // no clue why this is required, but omitting won't display a vertical scroll bar
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignContent: "flex-start",
    },
    bottom: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 'auto',
        padding: '10px 10px 10px 15px',
        height: '43px',
        borderTop: "1px solid #dddddd",
    },
    item: {
        width: "33%",
        [theme.breakpoints.down('md')]: {
            width: "50%",
        },
        padding: theme.spacing(1),
        height: "150px"
    },
    pagination: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center'
    },
    paper: {
        display: "flex",
        flex: "1 1 0",
        minHeight: 0,
        margin: 10,
        boxShadow: "0px 0px 2px 2px rgba(0, 0, 0, 0.1)",
        zIndex: 2,
        flexDirection: 'column',
        position: "relative"
    },
    paperwithOutBackground: {
        background: 'transparent',
        boxShadow: 'none',
        margin: 0,
        display: "flex",
        flex: "1 1 0",
        minHeight: 0,
        zIndex: 2,
        flexDirection: 'column',
        position: "relative"
    },
    table_action_header: {
        width: '120px',
        textAlign: 'center'

    },
    table_title_header: {
        width: "60%",
        minWidth: "100px",
    },
    table_project_header: {
        width: '20%',
        minWidth: "100px",
        padding: "0 5px",
    },
    projectTag: {
        backgroundColor: getModuloColor(1),
        borderRadius: 3,
        paddingLeft: 5,
        paddingRight: 5,
        flex: "0 0 auto",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    table_credits_header: {
        width: '60px',
        textAlign: "center",
        cursor: "default",
    },
    table_status_header: {
        width: '50px',
        textAlign: "center",
    },
    table_date_header: {
        width: '120px',
        cursor: "default",
    },
    table_owner_header: {
        width: '40%',
        minWidth: "150px",
    },
    reportContainerSize: {
        minWidth: (state) => {
            return state.loadingSource ? '320px' : '25%';
        },
        width: '25%',
        maxWidth: '25%',
        backgroundColor: '#ffffff',
        // opacity: 0.7,
    },
    table_project_cell: {
        display: "flex",
        overflow: "hidden",
    }
}))

export default function LibraryOverviewComponent(props) {
    const state = useContext(AppContext);
    const s = state.strings;
    const dispatch = useContext(DispatchContext);
    const classes = useStyles(state);
    const [librariesLoading, setLibrariesLoading] = useState(false);

    useEffect(() => {
        dispatch(actions.setUI({ libraryMenuExpanded: true }));
        loadLibraries();
    }, [state.librariesChangeNo]);

    if (window && window.location && state.ui && state.ui.pageName !== window.location.pathname) {
        dispatch(actions.navigate_to(window.location.pathname));
    }

    function loadLibraries() {
        setLibrariesLoading(true);
        Service.getLibraries({ filters: state.libraryFilter, ...state.libraryPagination, pagesize: state.user.settings.pagesize }).then(res => {
            if (res.success) {
                setLibrariesLoading(false);
                dispatch(actions.setLibtexts(res || []))
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });

        if (state.activeLibraryId) {
            Service.getLibrary(state.activeLibraryId).then(res => {
                if (res.success) {
                    dispatch(actions.get_library(res.data || []));
                } else {
                    dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
                }
            });
        }
    }

    const onChangePage = (page, pageLength) => {
        dispatch(actions.change_library_pagination({
            ...state.libraryPagination,
            pagesize: pageLength,
            pageno: page
        }));
    }

    const columns = useMemo(() => [
        {
            Header: ({ getToggleAllRowsSelectedProps }) => <Checkbox size="small" {...getToggleAllRowsSelectedProps()} />,
            accessor: "Active",
            Cell: ({ row }) => <Checkbox size="small" onClick={(e) => e.stopPropagation()} {...row.getToggleRowSelectedProps()} />,
            headerClassName: classes.table_status_header,
            cellClassName: classes.table_status_header,
            disableSortBy: true,
        },
        {
            Header: s.date,
            accessor: "Date",
            Cell: ({ cell: { value } }) => <>{timeConverter(value, null, s.common.dateFormat)}</>,
            headerClassName: classes.table_date_header,
            cellClassName: classes.table_date_header,
        },
        ...(state.user && state.user.license && state.user.userId == state.user.license.adminUserId ? [{
            Header: s.common.owner,
            accessor: "OwnerMail",
            Cell: ({ cell: { value } }) => <>{value || ""}</>,
            headerClassName: classes.table_owner_header,
            cellClassName: classes.table_owner_header,
        }] : []),
        {
            Header: s.name,
            accessor: "Name",
            cellClassName: classes.table_title_header,
            headerClassName: classes.table_title_header,
            Cell: ({ cell: { value, row } }) => <Link style={{ cursor: "pointer" }} href={"/libtext/" + row.original.Id} target="_blank">{value}</Link>
        },
        // Comment out as this is deemed misleading without further explanations
        /*
        {
            Header: "Shared",
            accessor: "IsOwner",
            Cell: ({ cell: { value, row } }) => <div>
                {value == 0 ?
                    <IconButton disabled size="small"> <ShareIcon /></IconButton>
                    : "-"}
            </div>,
            headerClassName: classes.table_action_header,
            cellClassName: classes.table_action_header,
        },*/
        {
            Header: s.project,
            accessor: "ProjectName",
            headerClassName: classes.table_project_header,
            cellClassName: classes.table_project_header,
            Cell: ({ cell: { value, row } }) => <div className={classes.table_project_cell}><div style={{ backgroundColor: getModuloColor(row.original.ProjectId) }} className={classes.projectTag}>{value}</div></div>,
        },
        {
            Header: s.words,
            accessor: "Words",
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
        },
        {
            Header: s.pages,
            accessor: "Pages",
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
        },
        {
            Header: s.libraryOverview.fulltext,
            accessor: "Fingerprint",
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
            Cell: ({ cell: { value, row } }) => <>{value ? <FingerprintIcon data-for="globalTT" data-tip={s.libraryOverview.fingerprintTip} /> : <DescriptionOutlinedIcon data-for="globalTT" data-tip={s.libraryOverview.fulltextTip} />}</>,
        },
        {
            Header: s.actions,
            accessor: s.actions,
            headerClassName: classes.table_action_header,
            cellClassName: classes.table_action_header,
            disableSortBy: true,
            Cell: ({ cell: { value, row } }) =>
                <div>
                    <IconButton disabled={!!row.original.Fingerprint} data-for="globalTT" data-tip={s.libraryOverview.performCheckOnThis} size="small" onClick={(e) => onMagnifyRow(e, row.original)}>
                        <SearchIcon />
                    </IconButton>
                    <IconButton data-for="globalTT" data-tip={s.libraryOverview.deleteThisTip} size="small"
                        onClick={(e) => onDeleteLibrary(e, row.original)} disabled={(row.original.IsOwner == 0)}>
                        <DeleteForeverIcon />
                    </IconButton>
                </div>,
        },
    ], [s.date] // react on language change in order to reflect new date format
    );

    const onDeleteLibrary = (e, row) => {
        e && e.stopPropagation();
        dispatch(actions.openConfirmationDialog({
            title: s.libraryOverview.deleteText,
            text: s.libraryOverview.deleteTextDesc,
            onConfirm: () => deleteLibraries([row.Id])
        }));
    }

    const deleteMultipleRow = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.libraryOverview.deleteTexts.format(ownedTextsSelected().length),
            text: s.libraryOverview.deleteTextsDesc,
            onConfirm: () => deleteLibraries(state.selectedLibraryIds)
        }));
    }

    const magnifyMultipleRow = () => {
        let data = state.libraries.filter((item) => {
            return state.selectedLibraryIds.includes(item.Id);
        });
        dispatch(actions.setNewCheckSettings({ source: 'library', libraryTexts: data }));
        dispatch(actions.navigate_to('/plagiarism/new'));
    }

    const compareMultipleRow = () => {
        const data = state.libraries.filter((item) => state.selectedLibraryIds.includes(item.Id));
        dispatch(actions.setPendingCollusionLibtexts(data));
        dispatch(actions.navigate_to('/tools/comparisons'));
    }

    const fingerprintMultipleRow = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.libraryOverview.performFingerprintOnSelectedTitle,
            text: s.libraryOverview.performFingerprintOnSelectedDesc.format(ownedNonFingerPrintTextsSelected().length),
            onConfirm: () => onConfirmFingerprint()
        }));
    }

    const onMagnifyRow = (e, row) => {
        e && e.stopPropagation();
        dispatch(actions.setNewCheckSettings({ source: 'library', libraryTexts: [row] }));
        dispatch(actions.navigate_to('/plagiarism/new'));
    }

    const deleteLibraries = (libraryids) => {
        Service.deleteLibraries(libraryids).then(res => {
            if (res && res.success) {
                // Clear active library if it was among the deleted ones
                if (state.activeLibraryId && libraryids.includes(state.activeLibraryId)) {
                    dispatch(actions.setActiveLibrary(null));
                    dispatch(actions.get_library([]));
                }
                dispatch(actions.notifyLibrariesChanged());
                dispatch(actions.openToast({ type: "success", message: s.notificationMessage.libtextsDeleted.format(res.total), status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }
    const onConfirmFingerprint = () => {
        // providing dummy values for date and name to satisfy CommandDispatcher. Thes will be ignored because they're filtered out by field set to fingerprint
        nonFingerPrintTextsSelected().length && Service.UpdateLibraries({ fingerprint: 1, field: 'fingerprint', date: 12345, name: 'dummy' }, nonFingerPrintTextsSelected()).then(res => {
            if (res.success) {
                dispatch(actions.notifyLibrariesChanged());
                dispatch(actions.openToast({ type: "success", message: s.notificationMessage.libtextsChangedToFingerprint.format(res.total), status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }


    // handle active row changed
    const onActiveRowHandle = async (rowDetails, index) => {
        if (rowDetails) {
            dispatch(actions.setActiveLibrary(rowDetails.Id));       // immediate feedback to table 
            Service.getLibrary(rowDetails.Id).then(res => {    // load details for new active report, this updates the details panel
                if (res.success) {
                    dispatch(actions.get_library(res.data || []));
                } else {
                    dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
                }                
            });
        } else {
            dispatch(actions.setActiveLibrary(null));
        }
    }

    const rowSelectedHandle = (rows) => {
        dispatch(actions.changeLibrarySelection(rows.map(r => r.Id)));
    }

    const handleSort = (sortBy) => {
        const sortByDetails = sortBy[0] || {
            id: 'Date',
            desc: true
        }
        const libraryFilter = {
            ...state.libraryPagination,
            sortby: sortByDetails.id,
            sortdir: sortByDetails.desc === true ? 'desc' : 'asc'
        }
        dispatch(actions.change_library_pagination(libraryFilter));
    }

    const nonFingerPrintTextsSelected = () => (state.libraries.filter((lib) => {
        return (state.selectedLibraryIds.includes(lib.Id)) && (lib.Fingerprint == 0)
    }).map(lib => lib.Id));

    const ownedTextsSelected = () => (state.libraries.filter((lib) => {
        return (state.selectedLibraryIds.includes(lib.Id)) && (lib.IsOwner == 1)
    }).map(lib => lib.Id));

    const ownedNonFingerPrintTextsSelected = () => (state.libraries.filter((lib) => {
        return (state.selectedLibraryIds.includes(lib.Id)) && (lib.IsOwner == 1 && lib.Fingerprint == 0)
    }).map(lib => lib.Id));

    const downloadCSVButtonClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.libraryOverview.exportCSVTitle,
            text: s.libraryOverview.exportCSVDesc,
            onConfirm: () => downloadCSV(),
            waitingMessage: s.libraryOverview.exportCSVWaiting
        }));
    }

    const downloadCSV = () => {
        Service.getLibraries({ filters: state.libraryFilter, ...state.libraryPagination, ExportAsCSV: true }).then(res => {
            dispatch(actions.closeConfirmationDialog());
            if (res.success) {
                const url = config.backendUrl + "export/?csv";
                clickDownloadLink(url);
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }



    return (
        <div className={classes.root}>
            {state.openCreateProjectPopup && <NewProjectForm />}
            <LibraryTitle className={classes.title} title={s.library} type="LibraryOverview" />
            <div className={classes.body_container}>
                <Paper elevation={3} className={state.performCheckLayout === 'list' ? classes.paper : classes.paperwithOutBackground}>
                    <LoadingScreen status={librariesLoading} />
                    <div className={classes.content}>
                        {state.libraries.length > 0 ?
                            <Table
                                data={state.libraries}
                                onSelectedRowsChange={rowSelectedHandle}
                                selectedIds={state.selectedLibraryIds}
                                columns={columns}
                                onActiveRowChanged={onActiveRowHandle}
                                activeRowId={state.activeLibraryId}
                                onSort={handleSort}
                                onSortBy={state.libraryPagination}
                            />
                            : (!librariesLoading && <IconLabel centered label={s.libraryOverview.noLibtextsFound} spacing={32} />)
                        }
                    </div>

                    <div className={classes.bottom}>

                        {state.selectedLibraryIds.length > 0 &&
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body2" color="primary">{s.libraryOverview.selectedTexts.format(state.selectedLibraryIds.length)}</Typography>
                                <IconButton disabled={(nonFingerPrintTextsSelected().length > 0) ? false : true} data-for="globalTT" data-tip={s.libraryOverview.performCheckOnSelected} style={{ marginLeft: 8 }} size="small" onClick={magnifyMultipleRow}><SearchIcon /></IconButton>
                                <IconButton disabled={state.selectedLibraryIds.length < 2} data-for="globalTT" data-tip="Compare selected texts" size="small" onClick={compareMultipleRow}><CompareArrowsIcon /></IconButton>
                                <IconButton disabled={!(ownedNonFingerPrintTextsSelected().length > 0)} data-for="globalTT" data-tip={s.libraryOverview.performFingerprintOnSelected} size="small" onClick={fingerprintMultipleRow}><FingerprintIcon /></IconButton>
                                <IconButton disabled={!(ownedTextsSelected().length > 0)} data-for="globalTT" data-tip={s.libraryOverview.deleteSelectedTip} size="small" onClick={deleteMultipleRow}> <DeleteForeverIcon /></IconButton>
                            </div>
                        }

                        <div className={classes.pagination}>
                            {state.libraries.length > 0 &&
                            <IconButton data-for="globalTT" data-tip={s.libraryOverview.exportCSVTip} size="small" onClick={downloadCSVButtonClicked}>
                                <CloudDownloadIcon />
                            </IconButton>}
                            <Pagination
                                page={state.libraryPagination.pageno}
                                pageLength={state.user.settings.pagesize}
                                totalRecords={state.totalLibrariesNumber || 0}
                                onchange={onChangePage}
                            />
                        </div>
                    </div>
                </Paper>
                <div className={classes.reportContainerSize}  >
                    <LibraryDetails libtext={state.activeLibrary} startPlagCheckHandler={onMagnifyRow} />
                </div>
            </div>
        </div>
    )
}